import React, { useState } from 'react'
import type { Building, Unit } from '../data/mockSpaceYieldData'
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, ChevronRight, AlertCircle, Droplets } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface YieldDetailTableProps {
  buildings: Building[]
  includeFuel: boolean
}

export const YieldDetailTable: React.FC<YieldDetailTableProps> = ({ buildings, includeFuel }) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // Formatters
  const formatCurrency = (val?: number) => val ? `$${Math.round(val).toLocaleString()}` : '—'
  const formatYield = (val: number) => `$${val.toFixed(2)}`
  
  // Status Badge Helper
  const getStatusBadge = (status: Unit['status']) => {
    switch (status) {
      case 'leased': return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200">🟢 Leased</Badge>
      case 'underpriced': return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200">🟠 Under</Badge>
      case 'expiring': return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200">🟡 Expir.</Badge>
      case 'month_to_month': return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200">🟣 M2M</Badge>
      case 'transient': return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">🔵 Trans</Badge>
      case 'occupied_no_contract': return <Badge className="bg-red-100 text-red-800 hover:bg-red-200">🔴 Occupied</Badge>
      case 'idle': return <Badge variant="outline" className="text-slate-500 border-slate-300">⬜ Idle</Badge>
      case 'maintenance': return <Badge variant="outline" className="bg-slate-100 text-slate-800">⬛ Maint</Badge>
      default: return <Badge variant="outline">{status}</Badge>
    }
  }

  return (
    <div className="rounded-md border bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="w-[100px]">Unit</TableHead>
            <TableHead className="w-[100px] text-right">Sqft</TableHead>
            <TableHead className="w-[140px]">Status</TableHead>
            <TableHead className="min-w-[150px]">Tenant</TableHead>
            <TableHead className="text-right w-[100px] font-medium text-slate-700">Lease Rate</TableHead>
            <TableHead className="text-right w-[100px] font-medium text-slate-700">Trans. Rev</TableHead>
            {includeFuel && <TableHead className="text-right w-[100px] font-medium text-amber-700">Fuel Rev</TableHead>}
            <TableHead className="text-right w-[120px] font-bold text-slate-900 border-l border-r bg-slate-100/50">
              {includeFuel ? 'Value / Sqft' : 'Yield / Sqft'}
            </TableHead>
            <TableHead className="w-[120px]">Contract End</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {buildings.map(building => {
            const isExpanded = expandedGroups[building.id] !== false // Default open?
            
            return (
              <React.Fragment key={building.id}>
                {/* Group Header Row */}
                <TableRow 
                    className="bg-slate-50 hover:bg-slate-100 cursor-pointer font-medium"
                    onClick={() => toggleGroup(building.id)}
                >
                  <TableCell colSpan={8} className="p-2 pl-4">
                    <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        <span className="text-slate-800 font-semibold">{building.name}</span>
                        <span className="text-slate-500 font-normal text-sm ml-2">
                            {building.total_units} units · {building.total_sqft.toLocaleString()} sqft · {building.occupancy_pct}% occ.
                        </span>
                        <span className="ml-auto text-slate-600 text-sm font-medium mr-4">
                            {formatCurrency(includeFuel ? building.total_value : building.revenue)}/mo {includeFuel ? 'value' : 'yield'} · {formatYield(includeFuel ? building.avg_total_yield : building.avg_yield)}/sqft avg
                        </span>
                    </div>
                  </TableCell>
                </TableRow>
                
                {/* Unit Rows */}
                {isExpanded && building.units.map((unit) => (
                  <TableRow key={unit.id} className="hover:bg-slate-50/50">
                    <TableCell className="font-medium text-slate-900">{unit.code}</TableCell>
                    <TableCell className="text-right text-slate-600">{unit.sqft.toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(unit.status)}</TableCell>
                    <TableCell className="text-slate-700">
                        <div className="font-medium flex items-center gap-1.5">
                            {unit.tenant_name || '—'}
                            {includeFuel && unit.fuel_gal_target && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1 py-0.5 rounded border border-amber-200 cursor-help">
                                                <Droplets className="w-3 h-3" />
                                                {(unit.fuel_gal_pumped || 0) / unit.fuel_gal_target > 0.9 ? 'Met' : 'Risk'}
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p className="font-semibold text-xs">Fuel Commitment</p>
                                            <p className="text-xs">{unit.fuel_gal_pumped} / {unit.fuel_gal_target} gal pumped</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        {unit.aircraft && <div className="text-xs text-slate-500">{unit.aircraft}</div>}
                    </TableCell>
                    <TableCell className="text-right text-slate-600 font-mono">
                        {formatCurrency(unit.lease_rate)}
                    </TableCell>
                    <TableCell className="text-right text-blue-600 font-mono font-medium">
                        {formatCurrency(unit.transient_revenue)}
                    </TableCell>
                    {includeFuel && (
                      <TableCell className="text-right text-amber-600 font-mono font-medium">
                          {formatCurrency(unit.fuel_revenue)}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-bold text-slate-900 bg-slate-50/30 border-l border-r font-mono">
                        {includeFuel 
                          ? formatYield((unit.total_value || 0) / unit.sqft) 
                          : formatYield(unit.yield_per_sqft)}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                        {unit.contract_end || '—'}
                        {unit.days_to_expiry && (
                            <div className="text-xs text-rose-500 font-medium flex items-center gap-1 mt-0.5">
                                <AlertCircle className="w-3 h-3" />
                                {unit.days_to_expiry} days
                            </div>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            )
          })}
          
          {/* Grand Total Footer */}
            <TableRow className="bg-slate-900 hover:bg-slate-900 text-white font-bold text-base border-t-2 border-slate-900">
                <TableCell colSpan={4} className="pl-4 py-4">
                    FACILITY TOTAL
                </TableCell>
                <TableCell className="text-right py-4 font-mono text-emerald-300">
                   {/* Lease Total - simplified sum for demo */}
                   $47,200
                </TableCell>
                <TableCell className="text-right py-4 font-mono text-blue-300">
                   {/* Transient Total */}
                   $5,600
                </TableCell>
                {includeFuel && (
                  <TableCell className="text-right py-4 font-mono text-amber-300">
                     $25,600
                  </TableCell>
                )}
                <TableCell className="text-right py-4 font-mono text-white bg-slate-800 border-l border-slate-700">
                   {/* Yield/Sqft Total */}
                   {includeFuel ? '$1.35' : '$0.91'}
                </TableCell>
                <TableCell className="py-4 text-xs font-normal text-slate-400 pl-4">
                    $8.6K opportunity
                </TableCell>
            </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
